import React, { useEffect, useRef, useState } from 'react';
import { brandBlue, textDark, border, white } from '../styles/theme';
import VideoPlayer from './VideoPlayer';

const BeforeAfterVideo = ({ leftSrc, rightSrc, leftLabel = 'Original', rightLabel = 'Upscaled', showLabels = true }) => {
  const containerRef = useRef(null);
  const leftVideoRef = useRef(null);
  const rightVideoRef = useRef(null);
  const isDraggingRef = useRef(false);
  const [clipPercent, setClipPercent] = useState(50);

  useEffect(() => {
    let cleanup = null;
    let intervalId = null;

    const tryAttach = () => {
      const left = leftVideoRef.current;
      const right = rightVideoRef.current;
      if (!left || !right) return false;

      const handleTimeUpdate = () => {
        if (Math.abs(left.currentTime - right.currentTime) > 0.06) {
          console.log('.. syncing', left.currentTime, right.currentTime, Math.abs(left.currentTime - right.currentTime));
          left.currentTime = right.currentTime;
          right.style.visibility = 'visible';
        }
      };

      right.addEventListener('timeupdate', handleTimeUpdate);

      cleanup = () => {
        right.removeEventListener('timeupdate', handleTimeUpdate);
      };

      return true;
    };

    if (!tryAttach()) {
      intervalId = setInterval(() => {
        if (tryAttach()) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }, 100);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (cleanup) cleanup();
    };
  }, [leftSrc, rightSrc]);

  const startDrag = (e) => {
    e.preventDefault();
    isDraggingRef.current = true;
  };

  const endDrag = () => {
    isDraggingRef.current = false;
  };

  const updateFromClientX = (clientX) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const clamped = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setClipPercent((clamped / rect.width) * 100);
  };

  const handleMouseMove = (e) => {
    if (!isDraggingRef.current) return;
    updateFromClientX(e.clientX);
  };

  const handleTouchMove = (e) => {
    if (!isDraggingRef.current) return;
    if (e.touches && e.touches[0]) updateFromClientX(e.touches[0].clientX);
  };

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full overflow-hidden select-none"
      onMouseMove={handleMouseMove}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      onTouchMove={handleTouchMove}
      onTouchEnd={endDrag}
    >
      {showLabels && (
        <>
          <div className="absolute top-3 left-3 z-20 pointer-events-none">
            <span
              className="rounded-full px-2.5 py-1 text-xs"
              style={{ background: white, border: `1px solid ${border}`, color: textDark }}
            >
              {leftLabel}
            </span>
          </div>
          <div className="absolute top-3 right-3 z-20 pointer-events-none">
            <span
              className="rounded-full px-2.5 py-1 text-xs"
              style={{ background: white, border: `1px solid ${border}`, color: textDark }}
            >
              {rightLabel}
            </span>
          </div>
        </>
      )}
      <div className="absolute inset-0">
        <VideoPlayer
          src={leftSrc}
          playerRef={leftVideoRef}
          controls={false}
          muted={true}
          autoPlay={true}
          loop={true}
          playsInline={true}
          initialSeek={1}
          containerClassName="w-full h-full"
          videoClassName="h-full w-full object-cover"
        />
      </div>
      <VideoPlayer
        src={rightSrc}
        playerRef={rightVideoRef}
        controls={false}
        muted={true}
        autoPlay={true}
        loop={true}
        playsInline={true}
        initialSeek={1}
        containerClassName="absolute inset-0 z-10"
        videoClassName="h-full w-full object-cover"
        videoStyle={{ clipPath: `inset(0% 0% 0% ${clipPercent}%)`, visibility: 'hidden' }}
      />
      <div
        className="absolute inset-y-0 z-20"
        style={{ left: `calc(${clipPercent}% - 12px)` }}
      >
        <div
          className="relative h-full w-6 cursor-col-resize"
          onMouseDown={startDrag}
          onTouchStart={startDrag}
        >
          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px" style={{ background: brandBlue }} />
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full px-2 py-1 text-[10px]"
            style={{ background: white, border: `1px solid ${border}`, color: textDark }}
          >
            ↔
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeforeAfterVideo;


